import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Linkedin, Globe } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

const ContactSection = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: 'Message Sent!',
      description: 'Thank you for contacting us. We will get back to you soon.',
    });
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      content: 'info@sperah.com',
      link: 'mailto:info@sperah.com',
    },
    {
      icon: Phone,
      title: 'Phone',
      content: '+1 (555) 123-4567',
      link: 'tel:+15551234567',
    },
    {
      icon: MapPin,
      title: 'Office',
      content: 'Business District, Global City',
      link: null,
    },
  ];

  return (
    <section id="contact" className="py-24 bg-gradient-to-br from-[#F5F1E8] via-white to-[#FFF8F0] relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#8B2635]/5 to-transparent rounded-full blur-3xl animate-float" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-[#D4A574]/5 to-transparent rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Get In <span className="text-[#8B2635]">Touch</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#8B2635] to-[#A63446] mx-auto mb-8" />
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Let's discuss how we can help transform your project management approach and drive success.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="border-0 shadow-2xl bg-white animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                    required
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="How can we help?"
                    required
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project..."
                    required
                    rows={5}
                    className="w-full"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#8B2635] to-[#A63446] hover:from-[#A63446] hover:to-[#8B2635] text-white font-semibold py-6 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  <Send className="mr-2 h-5 w-5" />
                  Send Message
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-8">
            {/* Contact Cards */}
            <div className="space-y-6">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                return (
                  <Card
                    key={index}
                    className="border-0 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 bg-white animate-fade-in-up"
                    style={{ animationDelay: `${0.2 + index * 0.1}s` }}
                  >
                    <CardContent className="p-6 flex items-center gap-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-[#8B2635] to-[#A63446] rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="h-7 w-7 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">{info.title}</h4>
                        {info.link ? (
                          <a
                            href={info.link}
                            className="text-gray-600 hover:text-[#8B2635] transition-colors"
                          >
                            {info.content}
                          </a>
                        ) : (
                          <p className="text-gray-600">{info.content}</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Social Links */}
            <Card className="border-0 shadow-lg bg-white animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
              <CardContent className="p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Connect With Us</h4>
                <div className="flex gap-4">
                  <button className="w-12 h-12 bg-gradient-to-br from-[#8B2635] to-[#A63446] rounded-lg flex items-center justify-center hover:shadow-lg transform hover:scale-110 transition-all duration-300">
                    <Linkedin className="h-6 w-6 text-white" />
                  </button>
                  <button className="w-12 h-12 bg-gradient-to-br from-[#8B2635] to-[#A63446] rounded-lg flex items-center justify-center hover:shadow-lg transform hover:scale-110 transition-all duration-300">
                    <Globe className="h-6 w-6 text-white" />
                  </button>
                  <button className="w-12 h-12 bg-gradient-to-br from-[#8B2635] to-[#A63446] rounded-lg flex items-center justify-center hover:shadow-lg transform hover:scale-110 transition-all duration-300">
                    <Mail className="h-6 w-6 text-white" />
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* CTA Card */}
            <Card className="border-0 shadow-lg bg-gradient-to-br from-[#8B2635] to-[#A63446] text-white animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
              <CardContent className="p-8">
                <h4 className="text-2xl font-bold mb-4">Ready to Start?</h4>
                <p className="mb-6 text-white/90 leading-relaxed">
                  Download our comprehensive presentation to learn more about our services and approach.
                </p>
                <Button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = 'https://customer-assets.emergentagent.com/job_744874e0-0fe3-4cd2-a918-10824da88281/artifacts/7oog39co_Sperah_Full_Combined_Presentation_Final.pptx';
                    link.download = 'Sperah_Project_Management_Presentation.pptx';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="w-full bg-white text-[#8B2635] hover:bg-gray-100 font-semibold shadow-lg"
                >
                  Download Presentation
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;